<script setup lang="ts">
import Num from '@/components/Num.vue';
import ProductPage from '@/components/ProductPage.vue';
import ProductPill from '@/components/ProductPill.vue';
import BuiltToLast from '@/components/aero-pro-cards/BuiltToLast.vue';
import Deck from '@/components/aero-pro-cards/Deck.vue';
import DriveTrain from '@/components/aero-pro-cards/DriveTrain.vue';
import ExtremeEfficiency from '@/components/aero-pro-cards/ExtremeEfficiency.vue';
import TrueHybridRide from '@/components/aero-pro-cards/TrueHybridRide.vue';
import StaysWithYou from '@/components/aero-pro-cards/StaysWithYou.vue';
import Trucks from '@/components/aero-pro-cards/Trucks.vue';
import UrbanUsability from '@/components/aero-pro-cards/UrbanUsability.vue';
import FastRecharge from '@/components/aero-pro-cards/FastRecharge.vue';
import ThaneLove from '@/components/aero-pro-cards/ThaneLove.vue';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { type Product, getProductById } from '@/lib/shopify/product.ts';
import { useShopifyPreload } from '@/composables/useShopifyPreload';
import { useSeoMeta } from '@unhead/vue';
import { useData } from 'vike-vue/useData';
import { onMounted, ref } from 'vue';
import { ArrowRightIcon } from '@radix-icons/vue';

const AERO_PRO_ID = 'gid://shopify/Product/8195298263262';

const data = useData<{ product?: Product }>();
const product = ref<Product | null>(data.product ?? null);
const { getPreloadedProductById } = useShopifyPreload();

// fetch fresh product data on mount as ssg may be stale
onMounted(async () => {
  // Try to get preloaded data first
  const preloadedProduct = await getPreloadedProductById(AERO_PRO_ID);
  if (preloadedProduct) {
    product.value = preloadedProduct;
  } else {
    // Fall back to regular fetch if no preloaded data
    product.value = await getProductById(AERO_PRO_ID);
  }
});

const cards = [
  BuiltToLast,
  ExtremeEfficiency,
  DriveTrain,
  TrueHybridRide,
  StaysWithYou,
  UrbanUsability,
  Deck,
  Trucks,
  ThaneLove,
  FastRecharge
];

useSeoMeta({
  title: () => (product.value?.title ? `${product.value.title} - Ultra-Lightweight Electric Skateboard | Parsec` : 'Aero Pro Electric Skateboard | Parsec Skateboards'),
  description: 'Experience the ultimate in portable electric skateboards with the Aero Pro. At just 15.5 pounds, featuring T800 carbon fiber deck, premium Molicel batteries, and unmatched power-to-weight ratio.',
  ogTitle: () => (product.value?.title ? `${product.value.title} - Premium Electric Skateboard` : 'Aero Pro - Premium Electric Skateboard by Parsec'),
  ogDescription: 'The lightest premium electric skateboard in its class. Built with aerospace-grade carbon fiber, 7075 aluminum trucks, and high-performance Molicel batteries for the perfect urban commute.',
  ogType: 'website',
  ogImage: () => product.value?.images?.nodes?.[0]?.url,
  twitterCard: 'summary_large_image',
  twitterTitle: () => (product.value?.title ? `${product.value.title} - Premium Electric Skateboard` : 'Aero Pro - Premium Electric Skateboard by Parsec'),
  twitterDescription: 'Ultra-lightweight electric skateboard featuring premium construction and unparalleled power. The perfect choice for urban commuting.',
  twitterImage: () => product.value?.images?.nodes?.[0]?.url
});
</script>

<template>
  <Head>
    <title>{{ product?.title ? `${product.title} - Ultra-Lightweight Electric Skateboard | Parsec` : 'Aero Pro Electric Skateboard | Parsec Skateboards' }}</title>
    <meta name="description" content="Experience the ultimate in portable electric skateboards with the Aero Pro. At just 15.5 pounds, featuring T800 carbon fiber deck, premium Molicel batteries, and unmatched power-to-weight ratio." />
    <meta property="og:title" :content="product?.title ? `${product.title} - Premium Electric Skateboard` : 'Aero Pro - Premium Electric Skateboard by Parsec'" />
    <meta property="og:description" content="The lightest premium electric skateboard in its class. Built with aerospace-grade carbon fiber, 7075 aluminum trucks, and high-performance Molicel batteries for the perfect urban commute." />
    <meta property="og:type" content="website" />
    <meta property="og:image" :content="product?.images?.nodes?.[0]?.url" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="product?.title ? `${product.title} - Premium Electric Skateboard` : 'Aero Pro - Premium Electric Skateboard by Parsec'" />
    <meta name="twitter:description" content="Ultra-lightweight electric skateboard featuring premium construction and unparalleled power. The perfect choice for urban commuting." />
    <meta name="twitter:image" :content="product?.images?.nodes?.[0]?.url" />
    <!-- Product schema markup -->
    <meta property="product:price:amount" :content="product?.priceRange?.minVariantPrice?.amount" />
    <meta property="product:price:currency" :content="product?.priceRange?.minVariantPrice?.currencyCode" />
    <meta property="product:availability" content="in_stock" />
  </Head>
  <ProductPage v-if="product" :product="product">
    <template #discount>
      <div>
        <p class="text-accent font-bold uppercase text-lg">BATCH 3 BACKORDER</p>
      </div>
    </template>

    <template #product-details="product">
      <template v-if="(product.variant?.title ?? '').startsWith('S')">
        <div class="flex gap-2 items-center">
          <div class="bg-accent w-2 h-2 rounded-full"></div>
          <h2>Batch 3 shipping in June.</h2>
        </div>
      </template>

      <template v-if="(product.variant?.title ?? '').startsWith('X')">
        <div class="flex gap-2 items-center">
          <div class="bg-accent w-2 h-2 rounded-full"></div>
          <h2>Batch 3 shipping in June.</h2>
        </div>
      </template>

      <template v-if="(product.variant?.title ?? '').startsWith('S')">
        <ProductPill>
          <h3 class="text-center"><Num value="15.5" unit="lb" :decimal="true" /> / <Num value="7" unit="kg" /></h3>
        </ProductPill>
        
        <ProductPill>
          <h3 class="text-center">Molicel P45B Battery</h3>
        </ProductPill>
      </template>
      <template v-else-if="(product.variant?.title ?? '').startsWith('X')">
        <ProductPill>
          <h3 class="text-center"><Num value="18.3" unit="lb" :decimal="true" /> / <Num value="8.3" unit="kg" :decimal="true" /></h3>
        </ProductPill>

        <ProductPill>
          <h3 class="text-center">Molicel P45B Battery</h3>
        </ProductPill>
      </template>
      <template v-else></template>

      <ProductPill>
        <h3 class="text-center">T800 Carbon Fiber Deck</h3>
      </ProductPill>

      <ProductPill>
        <h3 class="text-center">7075 Aluminum Trucks</h3>
      </ProductPill>

      <Button asChild variant="outline" size="lg" class="w-full group">
        <a href="https://support.rideparsec.com/compare/" target="_blank" rel="noopener" class="flex items-center justify-center gap-2">
          Complete Specs
          <ArrowRightIcon class="size-5 transition-transform group-hover:translate-x-1" />
        </a>
      </Button>
    </template>
    
    <div class="flex flex-col items-center justify-center w-full col-span-12 my-4 px-6">
      <p class="text-3xl text-center leading-10 max-w-3xl">
        <span class="font-medium text-white">Aero Pro is the epitome of lightweight electric skateboards</span>
        <span class="font-normal text-[#8e8e8e]">, combining premium construction with unparalleled power.</span>
      </p>
      <Button asChild variant="outline" size="lg" class="mt-6 group border-accent text-accent hover:bg-accent hover:text-black">
        <a href="https://support.rideparsec.com/2025/who-is-aero-pro-for/" target="_blank" rel="noopener" class="flex items-center justify-center gap-2">
          Who is Aero Pro for?
          <ArrowRightIcon class="size-5 transition-transform group-hover:translate-x-1" />
        </a>
      </Button>
    </div>

    <MasonryWall :items="cards" class="col-span-12" :max-columns="2" :gap="16">
      <template #default="{ item }">
        <component :is="item" v-fade-in class="col-span-6 min-h-[400px] max-h-min" />
      </template>
    </MasonryWall>

    <section v-fade-in class="box-contents bg-primary rounded-lg overflow-hidden col-span-12 md:col-span-4">
      <div class="w-full">
        <div class="w-full">
          <h1 class="text-2xl font-semibold mb-4">What's in the box</h1>
        </div>
        <ul>
          <li>Aero Pro Complete</li>
          <li>Remote Controller</li>
          <li>165W Standard Charger</li>
          <li>Skate Tool & Wrenches</li>
        </ul>
      </div>
    </section>

    <section v-fade-in class="faq bg-primary rounded-lg overflow-hidden col-span-12 md:col-span-8">
      <div class="w-full">
        <div class="w-full">
          <h1 class="text-2xl font-semibold mb-4">FAQ</h1>
        </div>

        <Accordion class="w-full" type="multiple" collapsible>
          <AccordionItem value="shipping">
            <AccordionTrigger>
              <h2>When will the Aero Pro ship?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Batch 2 pre-orders are expected to begin shipping near the end of March. For the latest shipping estimates, visit <a href="https://support.rideparsec.com" class="text-accent hover:underline">Parsec Support</a>.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="model">
            <AccordionTrigger>
              <h2>Which model should I get?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                The Aero Pro S and Aero Pro X differ only in battery capacity and weight. If most of your rides fall within the S model's range estimates and you often need to carry your board, the lighter weight may be a better fit. But if you don't mind an extra 2.8 pounds (1.3 kilograms), the Aero Pro X delivers more than double the range.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="remote">
            <AccordionTrigger>
              <h2>What remote does it use?</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Early batches will include a provisional but fully capable remote that other brands also use. Parsec's proprietary remote is in development and expected to ship later this year. Early batch customers will receive the new remote for free once it's released (customers cover customs duties only). The generic remote will remain fully functional and may be kept as a spare.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="more">
            <AccordionTrigger>
              <h2>I have more questions!</h2>
            </AccordionTrigger>
            <AccordionContent>
              <p>
                Check out our full FAQ at <a href="https://support.rideparsec.com" class="text-accent hover:underline">Parsec Support</a>.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>    
  </ProductPage>
</template>

<style>
.box-contents {
  @apply p-6;

  ul {
    @apply mt-4 w-full;
  }

  li {
    @apply text-lg py-4;
  
    &:not(:first-child) {
      @apply border-t border-neutral-700;
      margin-right: 1.5rem; /* Match the left margin from list-disc ml-6 */
    }
  }
}

.faq {
  @apply p-6;

  :deep(*) {
    @apply text-black dark:text-white;
  }

  .accordion-trigger {
    @apply flex items-center;
    
    h2 {
      @apply text-left text-lg;
    }

    :deep([data-state]) {
      @apply self-center;
    }
  }
}
</style>
